import { isPlatformBrowser } from "@angular/common";
import { HttpClient } from "@angular/common/http";
import { Component, DestroyRef, effect, inject, input, PLATFORM_ID, signal } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { GoogleMapsModule } from "@angular/google-maps";
import { TranslocoPipe } from "@jsverse/transloco";
import { map, Observable, of } from "rxjs";
import { catchError } from "rxjs/operators";
import { environment } from "../../../../../../environments/environment";
import { ItemsCoords } from "../../../../../api/packbo/models/items-coords";

@Component({
    selector: "packbo-supplier-map",
    templateUrl: "./supplier-map.template.html",
    imports: [GoogleMapsModule, TranslocoPipe]
})
export class SupplierMapComponent {
    private readonly platformId = inject(PLATFORM_ID);
    private readonly httpClient = inject(HttpClient);
    private readonly destroyRef = inject(DestroyRef);

    public readonly coordinate = input.required<ItemsCoords | null>();
    protected readonly apiLoaded = signal(false);

    protected readonly zoom = signal(15);
    protected readonly center = signal<google.maps.LatLngLiteral>({
        lat: 0,
        lng: 0
    });
    protected readonly markerPosition = signal<google.maps.LatLngLiteral>({
        lat: 0,
        lng: 0
    });
    protected readonly mapOptions = {
        mapTypeId: "roadmap",
        zoomControl: true,
        scrollwheel: true,
        maxZoom: 15,
        minZoom: 0,
        mapId: "supplier_map_id"
    };
    protected readonly markerOptions = {
        gmpDraggable: false
    };

    private readonly googleMapsApiKey = environment.googleMapsApiKey;

    constructor() {
        if (isPlatformBrowser(this.platformId)) {
            this.loadGoogleMapsApi()
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe(() => {
                    this.apiLoaded.set(true);
                });
        }

        effect(() => {
            const coords = this.coordinate();
            if (!coords || !this.apiLoaded()) {
                return;
            }

            if (isPlatformBrowser(this.platformId)) {
                this.geocodeAddress(coords);
            }
        });
    }

    private loadGoogleMapsApi(): Observable<boolean> {
        if (window.google?.maps) {
            return of(true);
        }

        return this.httpClient
            .jsonp(
                "https://maps.googleapis.com/maps/api/js?" +
                    `key=${this.googleMapsApiKey}` +
                    "&libraries=maps,marker,geometry",
                "callback"
            )
            .pipe(
                map(() => true),
                catchError(() => of(false))
            );
    }

    private async geocodeAddress(coords: ItemsCoords): Promise<void> {
        try {
            const address = this.buildAddressString(coords);

            const geocoder = new google.maps.Geocoder();

            const result = await geocoder.geocode({ address });

            if (result.results.length > 0) {
                const location = result.results[0].geometry.location;
                const position = {
                    lat: location.lat(),
                    lng: location.lng()
                };

                this.center.set(position);
                this.markerPosition.set(position);
            }
        } catch (error) {
            console.error("Geocoding failed:", error);
        }
    }

    private buildAddressString(coords: ItemsCoords): string {
        const parts = [coords.address1, coords.address2, coords.locality, coords.postal_code].filter(
            (part) => part && part.trim()
        );

        return parts.join(", ");
    }
}
